import { useState } from 'react'
import { isAxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import { EditSolidIcon, KeyIcon } from '@dhl-ui/icons'
import { Body1, Body2, Box, Card, Checkbox, Divider, Grid, H6, IconButton, Skeleton } from '@dhl-ui/components'

import { type ErrorResponse, useGetIntegrator, useGetIntegratorCCCApiCredentialsHook } from '@dhl/mdp-api'
import { useIsLoading, useNotificationMessage } from '~/common'
import { APICredentialsDialog } from '~/common/components/APICredentialsDialog'

type Props = {
  id: string
  onEdit: () => void
}

export const IntegratorCard = ({ id, onEdit }: Props) => {
  const { t } = useTranslation()
  const [apiClientId, setApiClientId] = useState<string>('')
  const [apiClientSecret, setApiClientSecret] = useState<string>('')
  const { isLoading, setIsLoading } = useIsLoading()

  const getData = useGetIntegratorCCCApiCredentialsHook()

  const { showError } = useNotificationMessage()

  const { data: integratorDetails } = useGetIntegrator(id)

  const generateApiCredentials = () => {
    setIsLoading(true)
    getData(id)
      .then((response) => {
        setApiClientId(response.clientId)
        setApiClientSecret(response.clientSecret)
      })
      .catch((error: ErrorResponse) => {
        if (isAxiosError(error)) {
          showError(error.message)
          setApiClientId('')
          setApiClientSecret('')
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return isLoading ? (
    <>
      <Skeleton height={216} />
      <Skeleton height={200} sx={{ mt: -3 }} />
    </>
  ) : (
    <>
      <Card>
        <Card.Header
          title={t('admin.integratorsPage.titleText')}
          endNode={
            <>
              <Grid container direction="row" spacing={1}>
                <Grid>
                  <IconButton aria-label="generate api credentials" onClick={generateApiCredentials}>
                    <KeyIcon />
                  </IconButton>
                </Grid>
                <Grid>
                  <Divider orientation="vertical" />
                </Grid>
                <Grid>
                  <IconButton aria-label="edit" data-testid="edit-button" onClick={() => onEdit()}>
                    <EditSolidIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </>
          }
        />
        <Card.Content>
          <Grid container direction="column" justifyContent="center" alignItems="stretch">
            <Grid size={{ sm: 12 }}>
              <Box>
                <H6>{t('admin.integratorsPage.accountInformation')}</H6>
              </Box>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={{ sm: 6 }}>
                <Box marginTop={2}>
                  <Body2 variant="tertiary">{t('admin.integratorsPage.name')}</Body2>
                  <Body1>{integratorDetails?.name}</Body1>
                </Box>
              </Grid>
              <Grid size={{ sm: 6 }}>
                <Box marginTop={2}>
                  <Body2 variant="tertiary">{t('admin.integratorsPage.description')}</Body2>
                  <Body1>{integratorDetails?.description}</Body1>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Box marginTop={2}>
            <Grid container direction="row" justifyContent="flex-start" alignItems="center">
              <Grid>
                <Checkbox
                  edge="start"
                  checked={integratorDetails?.limitedTrackTrace ? integratorDetails?.limitedTrackTrace : false}
                  name="limitedTrackTrace"
                  label={t('admin.integratorsPage.limited')}
                />
              </Grid>
            </Grid>
          </Box>
        </Card.Content>
      </Card>
      {apiClientId && apiClientSecret && (
        <APICredentialsDialog
          open={!!apiClientId && !!apiClientSecret}
          clientId={apiClientId}
          clientSecret={apiClientSecret}
          onClose={() => {
            setApiClientId('')
            setApiClientSecret('')
          }}
        />
      )}
    </>
  )
}
